import React from 'react';
import { Award, Clock, MapPin, Heart, CheckCircle } from 'lucide-react';

const About: React.FC = () => {
  const stats = [
    { number: '5+', label: 'Years Experience', icon: Clock },
    { number: '200+', label: 'Happy Clients', icon: Heart },
    { number: '100%', label: 'Satisfaction Rate', icon: Award },
    { number: '24/7', label: 'Support Available', icon: CheckCircle },
  ];

  const expertise = [
    'Property Management',
    'Tenant Relations',
    'Property Maintenance',
    'Interior Design',
    'Customer Service',
    'Local Market Knowledge'
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Content */}
          <div>
            <div className="mb-6 sm:mb-8">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                Meet Pattama
              </h2>
              <div className="flex items-center space-x-2 text-primary-600 mb-4 sm:mb-6">
                <MapPin size={18} className="sm:w-5 sm:h-5" />
                <span className="text-base sm:text-lg font-medium">Chiang Mai Real Estate Specialist</span>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6 text-gray-600 text-base sm:text-lg leading-relaxed">
              <p>
                With over 5 years of experience in Chiang Mai's real estate market, I specialize in 
                providing comprehensive property services that go beyond traditional real estate transactions. 
                My mission is to make your property journey as smooth and stress-free as possible.
              </p>
              
              <p>
                Whether you're a newcomer to Chiang Mai needing airport pickup and temporary housing, or 
                a property owner looking to enhance your investment with professional cleaning and landscaping, 
                I offer personalized solutions tailored to your specific needs.
              </p>
              
              <p>
                My deep understanding of the local market, combined with a network of trusted service providers, 
                ensures that every aspect of your real estate experience is handled with care and professionalism.
              </p>
            </div>

            {/* Expertise */}
            <div className="mt-6 sm:mt-8">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Areas of Expertise</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {expertise.map((skill, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle size={16} className="text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm sm:text-base">{skill}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8">
              <a
                href="#contact"
                className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Let's Work Together
              </a>
            </div>
          </div>

          {/* Image and Stats */}
          <div>
            {/* Profile Image Placeholder */}
            <div className="bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl p-8 mb-8 text-center">
              <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-6xl text-white font-bold">P</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Pattama</h3>
              <p className="text-primary-100">Licensed Real Estate Agent</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-lg mb-4">
                    <stat.icon size={24} />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Me?</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              I believe in building lasting relationships through trust, transparency, and exceptional service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart size={32} />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Personal Touch</h4>
              <p className="text-gray-600">Every client receives personalized attention and customized solutions.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={32} />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Always Available</h4>
              <p className="text-gray-600">24/7 support ensures you're never left without assistance.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award size={32} />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Proven Results</h4>
              <p className="text-gray-600">Track record of success with 100% client satisfaction rate.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About; 